import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppStore } from "@/core/store/useAppStore";
import { clientService } from "@/domains/Client/Client.service";
import { ClientLink } from "@/types";
import { Button } from "@/components/ui/Button";
import { Drawer } from "@/components/ui/Drawer";

interface AddLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  /** The client's current links; the new one is appended. */
  links: ClientLink[];
}

/**
 * Add a link to the client (Home's Command column). A create flow in the
 * shared Drawer (DD-051): the floating right panel on desktop, the bottom
 * sheet on phones. It keeps its modal-era name, so the column is unchanged.
 */
export const AddLinkModal: React.FC<AddLinkModalProps> = ({
  isOpen,
  onClose,
  clientId,
  links,
}) => {
  const queryClient = useQueryClient();
  const { setSelectedClient } = useAppStore();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmedName = name.trim();
    const trimmedUrl = url.trim();
    if (!trimmedName || !trimmedUrl) return;
    setSaving(true);
    try {
      const updated = await clientService.updateClient(clientId, {
        links: [...links, { name: trimmedName, url: trimmedUrl }],
      });
      setSelectedClient(updated);
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      setName("");
      setUrl("");
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Add Link"
      bodyPadding
      a11yDescription="A name and a URL for one of the client's links."
      className="md:max-w-md"
      footer={
        <>
          <Button
            type="button"
            size="sm"
            onClick={() => void handleSave()}
            disabled={!name.trim() || !url.trim()}
            isLoading={saving}
          >
            Add Link
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Instagram"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            URL
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
    </Drawer>
  );
};
